import { clsx, type ClassValue } from 'clsx';

export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

export function formatCurrency(value: number | undefined | null, currency = 'USD'): string {
  if (value === undefined || value === null) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatPercent(value: number | undefined | null, decimals = 2): string {
  if (value === undefined || value === null) return '+0.00%';
  return `${value >= 0 ? '+' : ''}${value.toFixed(decimals)}%`;
}

export function formatNumber(value: number | undefined | null, decimals = 2): string {
  if (value === undefined || value === null) return '0.00';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

export function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 30) return `${diffDay}d ago`;
  return formatDate(dateStr);
}

export function pnlColor(value: number): string {
  if (value > 0) return 'text-profit';
  if (value < 0) return 'text-loss';
  return 'text-surface-400';
}

export function pnlBgColor(value: number): string {
  if (value > 0) return 'bg-profit/10 text-profit';
  if (value < 0) return 'bg-loss/10 text-loss';
  return 'bg-surface-800 text-surface-400';
}

export function statusColor(status: string): string {
  switch (status) {
    case 'filled':
    case 'healthy':
    case 'connected':
    case 'active':
      return 'text-profit';
    case 'pending':
    case 'degraded':
      return 'text-warning';
    case 'cancelled':
    case 'rejected':
    case 'unhealthy':
    case 'disconnected':
      return 'text-loss';
    default:
      return 'text-surface-400';
  }
}

export function truncateMiddle(str: string, maxLen = 16): string {
  if (str.length <= maxLen) return str;
  const start = str.slice(0, Math.ceil(maxLen / 2) - 1);
  const end = str.slice(-(Math.floor(maxLen / 2) - 2));
  return `${start}...${end}`;
}

export function confidenceLabel(confidence: number): string {
  if (confidence >= 0.8) return 'Very High';
  if (confidence >= 0.6) return 'High';
  if (confidence >= 0.4) return 'Medium';
  if (confidence >= 0.2) return 'Low';
  return 'Very Low';
}

export function recommendationColor(rec: string): string {
  switch (rec) {
    case 'strong_buy':
      return 'text-profit font-bold';
    case 'buy':
      return 'text-profit';
    case 'hold':
      return 'text-warning';
    case 'sell':
      return 'text-loss';
    case 'strong_sell':
      return 'text-loss font-bold';
    default:
      return 'text-surface-400';
  }
}

/** Safe localStorage access that won't throw if storage is disabled or full */
export const safeStorage = {
  getItem(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem(key: string, value: string): boolean {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch {
      return false;
    }
  },
  removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  },
};

/** Ensure a value is always an array for safe .map() calls */
export function ensureArray<T>(value: T[] | undefined | null): T[] {
  return Array.isArray(value) ? value : [];
}
